/**
 * Service Worker Registration
 *
 * Registers the service worker for offline app shell caching.
 * Only active in production builds.
 */

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d{1,2})){3}$/)
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onError?: (error: unknown, context: { readyState: DocumentReadyState }) => void;
};

/**
 * What happened to the registration (#4029). Before, a failed register() was only a
 * console.error and the app carried on as if the worker had installed, so offline boot and
 * worker-driven updates were silently missing. 'failed' is now a state the app can read.
 *   skipped     — not a production build, or PUBLIC_URL is on another origin
 *   unsupported — this browser has no service workers (not a failure)
 *   pending     — waiting for the page to finish loading, or register() in flight
 *   registered  — navigator.serviceWorker.register() resolved
 *   failed      — it rejected (reported through onError)
 */
export type ServiceWorkerRegistrationState = 'skipped' | 'unsupported' | 'pending' | 'registered' | 'failed';

let registrationState: ServiceWorkerRegistrationState = 'skipped';

export function getServiceWorkerRegistrationState(): ServiceWorkerRegistrationState {
  return registrationState;
}

export function register(config?: Config) {
  if (process.env.NODE_ENV !== 'production') return;
  if (!('serviceWorker' in navigator)) {
    registrationState = 'unsupported';
    return;
  }
  // PUBLIC_URL is typed as string ONLY via react-app-env.d.ts (react-scripts).
  // client-tech compiles this file without that ambient, where it is
  // string | undefined. CRA substitutes a literal at build time, so '' is
  // behaviour-preserving here and strictly safer than stringifying undefined.
  const publicUrlBase = process.env.PUBLIC_URL ?? '';
  const publicUrl = new URL(publicUrlBase, window.location.href);
  if (publicUrl.origin !== window.location.origin) return;

  registrationState = 'pending';
  const start = () => {
    const swUrl = `${publicUrlBase}/service-worker.js`;

    if (isLocalhost) {
      // In localhost, check if SW exists then register
      checkValidServiceWorker(swUrl, config);
    } else {
      registerValidSW(swUrl, config);
    }
  };
  // Register once the page has fully loaded: registering earlier is the leading suspect for
  // the boot-time InvalidStateError (#4029). If `load` has ALREADY fired, a listener alone
  // never runs and the worker silently never registers, so start now instead.
  if (document.readyState === 'complete') {
    start();
  } else {
    window.addEventListener('load', start, { once: true });
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registrationState = 'registered';
      // A worker that finished installing in an EARLIER page (a tab left open for days) is
      // already waiting: no `updatefound` will fire for it here, so report it now or nothing
      // can ever activate it.
      if (registration.waiting && navigator.serviceWorker.controller) {
        config?.onUpdate?.(registration);
      }
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (!installingWorker) return;

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New content available — notify the app (a waiting worker exists).
              config?.onUpdate?.(registration);
            } else {
              // Content cached for offline use on first install.
              config?.onSuccess?.(registration);
            }
          }
        };
      };
    })
    .catch((error) => {
      registrationState = 'failed';
      const context = { readyState: document.readyState };
      if (config?.onError) {
        config.onError(error, context);
      } else {
        console.error('[SW] Registration failed:', error, context);
      }
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (response.status === 404 || (contentType && !contentType.includes('javascript'))) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => window.location.reload());
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      // Offline: no service worker script reachable. App runs from cache.
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => registration.unregister())
      .catch((error) => console.error(error.message));
  }
}
